import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  styled,
  Badge,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
} from '@mui/material';
import AppPaginationBar from "components/AppPaginationBar";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Comment as CommentIcon,
  ThumbUp as LikeIcon,
  Delete as DeleteIcon,
  CalendarToday as DateIcon,
  Category as CategoryIcon,
  Image as ImageIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import { H6 } from "components/Typography";
import { usePageTitle } from "contexts/PageTitleContext";
import { useDeleteConfirmation } from "contexts/DeleteConfirmationContext";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import buttonConfig from "config/ConfigAccessButton";

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
    transform: 'translateY(-2px)'
  }
}));

const PostCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease',
  minHeight: 360,
  display: 'flex',
  flexDirection: 'column',
  '& > *:last-child': {
    marginTop: 'auto'
  },
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
  }
}));

const CommentCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  borderRadius: theme.spacing(1),
  backgroundColor: alpha(theme.palette.background.default, 0.5),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

const SummaryCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '100px',
    height: '100px',
    background: `radial-gradient(circle, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 70%)`,
    borderRadius: '50%',
    transform: 'translate(30px, -30px)',
  }
}));

const Posts = () => {
  const theme = useTheme();
  const { setPageTitle } = usePageTitle();
  const { showDeleteConfirmation } = useDeleteConfirmation();
  
  // State management
  const [posts, setPosts] = useState([]);
  const [comments, setComments] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState({});
  const [likesDialogOpen, setLikesDialogOpen] = useState(false);
  const [likesLoading, setLikesLoading] = useState(false);
  const [likedUsersByPost, setLikedUsersByPost] = useState({});
  const [selectedLikesPost, setSelectedLikesPost] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
    next_page_number: null,
    previous_page_number: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 25, 50, 100];
  const serverPaginationRef = useRef(false);
  
  // Success notification state
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [successSeverity, setSuccessSeverity] = useState('success');
  
  // Summary stats
  const [summaryStats, setSummaryStats] = useState({
    totalPosts: 0,
    totalComments: 0,
    totalLikes: 0,
    postsWithImages: 0
  });

  // ============ PERMISSION FUNCTIONS ============
  const getCurrentButtonPermissions = () => {
    const moduleConfig = buttonConfig["Post"];
    return moduleConfig ? moduleConfig : {};
  };
  
  const shouldEnableButton = (buttonKey) => {
    const permissions = getCurrentButtonPermissions();
    return permissions[buttonKey] === true;
  };
  // ============ END PERMISSION FUNCTIONS ============

  useEffect(() => {
    setPageTitle('Posts & Comments Management');
    fetchPosts(1, 10);
  }, [setPageTitle]);

  // Search only when searchTerm changes (triggered by search button)
  useEffect(() => {
    fetchPosts(1, pageSize);
  }, [searchTerm]);

  useEffect(() => {
    // Filter posts based on search query (client-side for current page)
    if (!searchTerm.trim()) {
      setFilteredPosts(posts);
    } else {
      const filtered = posts.filter(post => 
        post.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.author_first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.author_last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.categories?.some(cat => cat.name?.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredPosts(filtered);
    }
  }, [searchTerm, posts]);

  const fetchPosts = async (pageParam = 1, pageSizeParam = 10) => {
    setLoading(true);
    setError('');
    
    try {
      let url = `${API_ENDPOINTS.GET_post}${API_ENDPOINTS.GET_post.includes('?') ? '&' : '?'}page=${pageParam}&page_size=${pageSizeParam}`;
      
      // Add search term to API call if present
      if (searchTerm.trim()) {
        url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }
      
      const response = await apiService.get(url);
      
      if (response?.success) {
        const postsData = response.data || [];
        setPosts(Array.isArray(postsData) ? postsData : []);
        setFilteredPosts(Array.isArray(postsData) ? postsData : []);
        const paginationData = response?.pagination || response?.data?.pagination || {};
        if (Object.keys(paginationData).length > 0) {
          serverPaginationRef.current = true;
          setPagination({
            current_page: paginationData.current_page ?? pageParam,
            page_size: paginationData.page_size ?? pageSizeParam,
            total_items: paginationData.total_items ?? 0,
            total_pages: paginationData.total_pages ?? 1,
            has_next: paginationData.has_next ?? false,
            has_previous: paginationData.has_previous ?? false,
            next_page_number: paginationData.next_page_number ?? null,
            previous_page_number: paginationData.previous_page_number ?? null,
          });
          setCurrentPage(paginationData.current_page ?? pageParam);
          setPageSize(paginationData.page_size ?? pageSizeParam);
        } else {
          serverPaginationRef.current = false;
          const list = Array.isArray(postsData) ? postsData : [];
          const total = list.length;
          const totalPages = Math.max(1, Math.ceil(total / pageSizeParam));
          setPagination(prev => ({ ...prev, total_items: total, total_pages: totalPages, current_page: 1, page_size: pageSizeParam }));
          setCurrentPage(1);
          setPageSize(pageSizeParam);
        }
        const list = Array.isArray(postsData) ? postsData : [];
        const paginationDataForStats = response?.pagination || response?.data?.pagination || {};
        const stats = {
          totalPosts: paginationDataForStats?.total_items ?? list.length,
          totalComments: list.reduce((sum, post) => sum + (post.comment_count || 0), 0),
          totalLikes: list.reduce((sum, post) => sum + (post.post_likes_count || 0), 0),
          postsWithImages: list.filter(post => post.image && post.image !== null && post.image.trim() !== '').length
        };
        setSummaryStats(stats);
      } else {
        throw new Error(response?.message || 'Failed to fetch posts');
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
    setHasSearched(true);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
    setHasSearched(false);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePostsPageChange = (_, value) => {
    setCurrentPage(value);
    if (serverPaginationRef.current) {
      fetchPosts(value, pageSize);
    }
  };

  const effectiveTotalPages = Math.max(1, Math.ceil((pagination.total_items || filteredPosts.length) / pageSize));
  const displayPosts = serverPaginationRef.current
    ? filteredPosts
    : filteredPosts.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const showPagination = effectiveTotalPages > 1;

  const handlePostsPageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    fetchPosts(1, newSize);
  };

  const fetchComments = async (postId) => {
    setCommentsLoading(true);
    try {
      const response = await apiService.get(`${API_ENDPOINTS.GET_comment}?post_id=${postId}`);
      
      if (response?.success) {
        setComments(prev => ({
          ...prev,
          [postId]: response.data || []
        }));
      } else {
        throw new Error(response?.message || 'Failed to fetch comments');
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      setError('Failed to load comments');
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleViewComments = async (post) => {
    setSelectedPost(post);
    setCommentsDialogOpen(true);
    
    // Fetch comments if not already loaded
    if (!comments[post.id]) {
      await fetchComments(post.id);
    }
  };

  const handleDeletePost = async (postId) => {
    const confirmed = await showDeleteConfirmation({
      title: "Delete Post",
      message: "Are you sure you want to delete this post? This action cannot be undone.",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel"
    });
    
    if (!confirmed) {
      return;
    }

    setDeleteLoading(prev => ({ ...prev, [postId]: true }));
    
    try {
      const response = await apiService.delete(`${API_ENDPOINTS.DELETE_post}?post_id=${postId}`);
      
      if (response?.success) {
        const deletedPost = posts.find(post => post.id === postId);
        
        setPosts(prev => prev.filter(post => post.id !== postId));
        setFilteredPosts(prev => prev.filter(post => post.id !== postId));
        
        if (deletedPost) {
          setSummaryStats(prev => ({
            ...prev,
            totalPosts: prev.totalPosts - 1,
            totalComments: prev.totalComments - (deletedPost.comment_count || 0),
            totalLikes: prev.totalLikes - (deletedPost.post_likes_count || 0),
            postsWithImages: prev.postsWithImages - (deletedPost.image && deletedPost.image !== null && deletedPost.image.trim() !== '' ? 1 : 0)
          }));
        }
        
        setSuccessMessage('Post deleted successfully!');
        setSuccessSeverity('success');
        setShowSuccess(true);
      } else {
        throw new Error(response?.message || 'Failed to delete post');
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      setError('Failed to delete post');
      
      setSuccessMessage('Failed to delete post. Please try again.');
      setSuccessSeverity('error');
      setShowSuccess(true);
    } finally {
      setDeleteLoading(prev => ({ ...prev, [postId]: false }));
    }
  };

  const handleDeleteComment = async (commentId, postId) => {
    const confirmed = await showDeleteConfirmation({
      title: "Delete Comment",
      message: "Are you sure you want to delete this comment? This action cannot be undone.",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel"
    });
    
    if (!confirmed) {
      return;
    }

    try {
      const response = await apiService.delete(`${API_ENDPOINTS.DELETE_comment}?comment_id=${commentId}`);
      
      if (response?.success) {
        setComments(prev => ({
          ...prev,
          [postId]: prev[postId]?.filter(comment => comment.comment_id !== commentId) || []
        }));
        
        setPosts(prev => prev.map(post => 
          post.id === postId 
            ? { ...post, comment_count: Math.max(0, (post.comment_count || 0) - 1) }
            : post
        ));
        
        setSummaryStats(prev => ({
          ...prev,
          totalComments: prev.totalComments - 1
        }));
        
        setSuccessMessage('Comment deleted successfully!');
        setSuccessSeverity('success');
        setShowSuccess(true);
      } else {
        throw new Error(response?.message || 'Failed to delete comment');
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      setError('Failed to delete comment');
      
      setSuccessMessage('Failed to delete comment. Please try again.');
      setSuccessSeverity('error');
      setShowSuccess(true);
    }
  };

  const fetchLikedUsers = async (postId) => {
    setLikesLoading(true);
    try {
      const userId = localStorage.getItem('user_id');
      const res = await apiService.get(`${API_ENDPOINTS.GET_post_liked_user_by_post_id}?user_id=${userId || ''}&post_id=${postId}`);
      const likedUsers = Array.isArray(res?.data?.liked_users)
        ? res.data.liked_users
        : Array.isArray(res?.liked_users)
          ? res.liked_users
          : [];
      setLikedUsersByPost(prev => ({ ...prev, [postId]: likedUsers }));
    } catch (err) {
      console.error('Error fetching liked users:', err);
      setLikedUsersByPost(prev => ({ ...prev, [postId]: [] }));
    } finally {
      setLikesLoading(false);
    }
  };

  const handleViewLikes = async (post) => {
    setSelectedLikesPost(post);
    setLikesDialogOpen(true);
    if (!likedUsersByPost[post.id]) {
      await fetchLikedUsers(post.id);
    }
  };

  const handleRefresh = () => {
    fetchPosts(currentPage, pageSize);
  };

  const handleCloseSuccess = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowSuccess(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase() || 'U';
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  if (loading && posts.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading posts data...</Typography>
      </Box>
    );
  }

  return (
    <Box pt={2} pb={4}>
      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSuccess} 
          severity={successSeverity}
          variant="filled"
          icon={<CheckCircleIcon fontSize="inherit" />}
          sx={{ 
            width: '100%',
            boxShadow: theme.shadows[3],
            fontWeight: 500
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Page Header */}
      <FlexBetween mb={3}>
        <Box>
          {/* <H6 fontSize={18} mb={0.5}>Posts & Comments Management</H6> */}
          {/* <Typography variant="body2" color="text.secondary">
            Manage and view posts, comments, and engagement statistics
          </Typography> */}
        </Box>
        {/* <Box display="flex" alignItems="center" gap={2}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box> */}
      </FlexBetween>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard>
            <FlexBetween>
              <Box>
                <Typography variant="h4" fontWeight={700} mb={0.5}>
                  {summaryStats.totalPosts}
                </Typography>
                <Typography variant="body2" opacity={0.8}>
                  Total Posts
                </Typography>
              </Box>
              <CommentIcon sx={{ fontSize: 40, opacity: 0.8 }} />
            </FlexBetween>
          </SummaryCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 3, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.1) }}>
            <FlexBetween>
              <Box>
                <Typography variant="h4" fontWeight={700} mb={0.5} color="success.dark">
                  {summaryStats.totalComments}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Comments
                </Typography>
              </Box>
              <Badge badgeContent="Comments" color="success"
              sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        top:-10,
                        right:20
                      },
                    }}>
                <CommentIcon sx={{ fontSize: 40, color: 'success.main' }} />
              </Badge>
            </FlexBetween>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 3, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.1) }}>
            <FlexBetween>
              <Box>
                <Typography variant="h4" fontWeight={700} mb={0.5} color="info.dark">
                  {summaryStats.totalLikes}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Likes
                </Typography>
              </Box>
              <LikeIcon sx={{ fontSize: 40, color: 'info.main' }} />
            </FlexBetween>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 3, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
            <FlexBetween>
              <Box>
                <Typography variant="h4" fontWeight={700} mb={0.5} color="warning.dark">
                  {summaryStats.postsWithImages}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Posts with Images
                </Typography>
              </Box>
              <ImageIcon sx={{ fontSize: 40, color: 'warning.main' }} />
            </FlexBetween>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <StyledCard sx={{ p: 3, mb: 3 }}>
        <FlexBetween mb={2}>
          <Typography variant="h6" fontWeight={600}>
            Posts Directory
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary">
              {filteredPosts.length} of {posts.length} posts
            </Typography>
          </Box>
        </FlexBetween>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by content, author, or category..."
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                if (hasSearched && e.target.value !== searchTerm) {
                  setHasSearched(false);
                }
              }}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch} edge="end">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { height: 40 }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: 40,
                  borderRadius: 2
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                disabled={!searchInput.trim() || (searchInput === searchTerm && hasSearched)}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: "none", 
                  height: 40,
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: 'none'
                  }
                }}
              >
                Search
              </Button>
              
              <FormControl size="small" sx={{ minWidth: 110 }}>
                <InputLabel>Rows</InputLabel>
                <Select value={pageSize} label="Rows" onChange={handlePostsPageSizeChange}>
                  {pageSizeOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box display="flex" alignItems="center" gap={2}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>  
            </Box>
          </Grid>
        </Grid>
      </StyledCard>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Posts Grid */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress size={40} />
          <Typography variant="body1" sx={{ ml: 2 }}>Loading posts...</Typography>
        </Box>
      ) : filteredPosts.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {searchTerm ? 'No posts found matching your search' : 'No posts available'}
          </Typography>
          {searchTerm && (
            <Button
              variant="text"
              onClick={handleClearSearch}
              sx={{ mt: 1 }}
            >
              Clear search
            </Button>
          )}
        </Card>
      ) : (
        <>
        <Grid container spacing={3}>
          {displayPosts.map((post) => (
            <Grid item xs={12} md={6} lg={4} key={post.id}>
              <PostCard sx={{ p: 3 }}>
                {/* Post Header */}
                <FlexBetween mb={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar
                      sx={{ 
                        width: 40, 
                        height: 40,
                        bgcolor: theme.palette.primary.main,
                        fontSize: '0.875rem',
                        fontWeight: 600
                      }}
                    >
                      {getInitials(post.author_first_name, post.author_last_name)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {`${post.author_first_name || ''} ${post.author_last_name || ''}`.trim() || 'Anonymous'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(post.created_at)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" gap={1}>
                    <Tooltip title={!shouldEnableButton('delete') ? "You don't have permission to delete post" : "Delete Post"}>
                      <span>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDeletePost(post.id)}
                          disabled={deleteLoading[post.id] || !shouldEnableButton('delete')}
                          sx={{ opacity: !shouldEnableButton('delete') ? 0.5 : 1 }}
                        >
                          {deleteLoading[post.id] ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </FlexBetween>

                {/* Post Image */}
                {post.image && (
                  <Box mb={2} position="relative" sx={{ overflow: 'hidden', borderRadius: theme.spacing(1) }}>
                    <img 
                      src={post.image}
                      alt="Post"
                      style={{
                        width: '100%',
                        height: 260,
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease',
                        display: 'block'
                      }}
                      onMouseOver={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                      onMouseOut={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                      onClick={() => window.open(post.image, '_blank')}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </Box>
                )}

                {/* Post Content */}
                <Typography variant="body2" mb={2} sx={{ lineHeight: 1.8 }}>
                  {truncateText(post.content, 220)}
                </Typography>

                {/* Categories */}
                {post.categories && post.categories.length > 0 && (
                  <Box mb={2} display="flex" gap={1} flexWrap="wrap">
                    {post.categories.map((category, index) => (
                      <Chip
                        key={index}
                        label={category.name}
                        size="small"
                        sx={{
                          backgroundColor: category.color || theme.palette.primary.main,
                          color: 'white',
                          fontSize: '0.7rem'
                        }}
                      />
                    ))}
                  </Box>
                )}

                {/* Post Stats */}
                <FlexBetween>
                  <Box display="flex" gap={2}>
                    <Tooltip title="View Likes">
                      <Box display="flex" alignItems="center" gap={0.5} style={{ cursor: shouldEnableButton('view_likes') ? 'pointer' : 'not-allowed' }} onClick={() => shouldEnableButton('view_likes') && handleViewLikes(post)}>
                        <LikeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'underline', opacity: shouldEnableButton('view_likes') ? 1 : 0.5 }}>
                          {post.post_likes_count || 0}
                        </Typography>
                      </Box>
                    </Tooltip>
                    <Box display="flex" alignItems="center" gap={0.5} style={{ cursor: shouldEnableButton('comment') ? 'pointer' : 'not-allowed' }} onClick={() => shouldEnableButton('comment') && handleViewComments(post)}>
                      <CommentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'underline', opacity: shouldEnableButton('comment') ? 1 : 0.5 }}>
                        {post.comment_count || 0}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    ID: {post.id}
                  </Typography>
                </FlexBetween>
              </PostCard>
            </Grid>
          ))}
        </Grid>
        <AppPaginationBar
          pagination={{
            current_page: currentPage,
            page_size: pageSize,
            total_items: pagination.total_items ?? filteredPosts.length,
            total_pages: effectiveTotalPages,
          }}
          onPageChange={handlePostsPageChange}
          itemLabel="posts"
        />
        </>
      )}

      {/* Comments Dialog */}
      <Dialog 
        open={commentsDialogOpen} 
        onClose={() => setCommentsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <FlexBetween>
            <Typography variant="h6" fontWeight={600}>
              Comments for Post #{selectedPost?.id}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {comments[selectedPost?.id]?.length || 0} comments
            </Typography>
          </FlexBetween>
        </DialogTitle>
        <DialogContent>
          {selectedPost && (
            <Box mb={3}>
              <Typography variant="body1" fontWeight={600} mb={1}>
                Post Content:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedPost.content}
              </Typography>
            </Box>
          )}
          
          <Divider sx={{ mb: 2 }} />
          
          {commentsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
              <CircularProgress size={30} />
              <Typography variant="body2" sx={{ ml: 2 }}>Loading comments...</Typography>
            </Box>
          ) : comments[selectedPost?.id]?.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
              No comments yet
            </Typography>
          ) : (
            <List>
              {comments[selectedPost?.id]?.map((comment) => (
                <CommentCard key={comment.comment_id} sx={{ mb: 2 }}>
                  <ListItem alignItems="flex-start">
                    <ListItemAvatar>
                      <Avatar
                        sx={{ 
                          width: 32, 
                          height: 32,
                          bgcolor: theme.palette.secondary.main,
                          fontSize: '0.75rem',
                          fontWeight: 600
                        }}
                      >
                        {getInitials(comment.author_first_name, comment.author_last_name)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {`${comment.author_first_name || ''} ${comment.author_last_name || ''}`.trim() || 'Anonymous'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(comment.created_at)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {comment.comment_content}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title={!shouldEnableButton('delete_comment') ? "You don't have permission to delete comment" : "Delete Comment"}>
                        <span>
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDeleteComment(comment.comment_id, selectedPost.id)}
                            disabled={!shouldEnableButton('delete_comment')}
                            sx={{ opacity: !shouldEnableButton('delete_comment') ? 0.5 : 1 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                </CommentCard>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentsDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Likes Dialog */}
      <Dialog 
        open={likesDialogOpen} 
        onClose={() => setLikesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <FlexBetween>
            <Typography variant="h6" fontWeight={600}>
              Likes for Post #{selectedLikesPost?.id}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {(likedUsersByPost[selectedLikesPost?.id]?.length) || 0} likes
            </Typography>
          </FlexBetween>
        </DialogTitle>
        <DialogContent>
          {likesLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
              <CircularProgress size={30} />
              <Typography variant="body2" sx={{ ml: 2 }}>Loading likes...</Typography>
            </Box>
          ) : (likedUsersByPost[selectedLikesPost?.id]?.length || 0) === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
              No likes yet
            </Typography>
          ) : (
            <List>
              {likedUsersByPost[selectedLikesPost?.id]?.map((user) => (
                <ListItem key={user.id} alignItems="flex-start">
                  <ListItemAvatar>
                    <Avatar src={user.profile_image || undefined} sx={{ bgcolor: theme.palette.info.main }}>
                      {getInitials(user.first_name, user.last_name)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Anonymous'}
                        </Typography>
                        {user.mobile_number && (
                          <Typography variant="caption" color="text.secondary">{user.mobile_number}</Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {user.email || user.village || ''}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLikesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Posts;